// FinVault encrypted backup helper.
// Uses WebCrypto AES-GCM with a PBKDF2-derived key from the user's backup password.

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
const DEFAULT_ITERATIONS = 250000;

function bytesToBase64(bytes) {
  let binary = "";
  bytes.forEach(byte => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function base64ToBytes(value = "") {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function randomBytes(length) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function isBackupEncryptionSupported() {
  return typeof crypto !== "undefined" && Boolean(crypto.subtle) && Boolean(crypto.getRandomValues);
}

async function deriveBackupKey(password, saltBase64, iterations = DEFAULT_ITERATIONS) {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    TEXT_ENCODER.encode(String(password || "")),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: base64ToBytes(saltBase64), iterations, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptBackupPayload(payload, password) {
  if (!isBackupEncryptionSupported()) throw new Error("This browser does not support WebCrypto backup encryption.");
  if (!password || String(password).length < 8) throw new Error("Backup password must be at least 8 characters.");

  const salt = bytesToBase64(randomBytes(16));
  const iv = randomBytes(12);
  const key = await deriveBackupKey(password, salt, DEFAULT_ITERATIONS);
  const plaintext = TEXT_ENCODER.encode(JSON.stringify(payload || {}));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);

  return {
    format: "finvault-encrypted-backup",
    version: 1,
    app: "FinVault",
    createdAt: new Date().toISOString(),
    encryption: {
      algorithm: "AES-GCM",
      keyDerivation: "PBKDF2-SHA256",
      iterations: DEFAULT_ITERATIONS,
      salt,
      iv: bytesToBase64(iv)
    },
    data: bytesToBase64(new Uint8Array(encrypted)),
    notice: "This backup is encrypted with your backup password. FinVault cannot recover this file if you forget the password. Import/restore is currently disabled."
  };
}

export async function decryptBackupPayload(envelope, password) {
  if (!isBackupEncryptionSupported()) throw new Error("This browser does not support WebCrypto backup decryption.");
  if (!envelope || envelope.format !== "finvault-encrypted-backup") throw new Error("Invalid FinVault encrypted backup file.");
  const enc = envelope.encryption || {};
  const key = await deriveBackupKey(password, enc.salt, Number(enc.iterations || DEFAULT_ITERATIONS));
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToBytes(enc.iv) },
    key,
    base64ToBytes(envelope.data)
  );
  return JSON.parse(TEXT_DECODER.decode(plain) || "{}");
}

export async function downloadEncryptedBackup(payload, password, fileNamePrefix = "FinVault_encrypted_backup") {
  const encrypted = await encryptBackupPayload(payload, password);
  const blob = new Blob([JSON.stringify(encrypted, null, 2)], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().split("T")[0];
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileNamePrefix}_${date}.fvault`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return encrypted;
}
